import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { style: { "display": "none" } };
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSettingsStore } from '@stores/settings.js';
/**
 * IntercomWidget - Renderless component that manages Intercom messenger lifecycle
 *
 * This component handles the lifecycle of the Intercom customer support widget.
 * Initialization is handled by the settings store once feature flags are loaded,
 * ensuring no race conditions. This component:
 * - Watches for changes in the INTERCOM_WIDGET feature flag
 * - Triggers Intercom initialization when the feature flag is enabled
 * - Controls the visibility of the Intercom messenger based on the route
 */
const INTERCOM_WIDGET_FLAG = 'INTERCOM_WIDGET';
/**
 * Routes where the Intercom widget launcher should be visible.
 * Only show on administrative/settings pages where users are most likely
 * to need support. Exclude asset/operational dashboards to avoid UI clutter.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'IntercomWidget',
    setup(__props) {
        const ROUTE_WHITELIST = [
            'error',
            'reports_select',
            'facility_list',
            'facility_detail',
            'facility_settings',
            'asset_settings',
            'asset_products',
            'user_list',
            'profile',
            'settings',
            'shift_list',
            'system_status',
            'device_management',
            'group_list',
            'product_categories'
        ];
        const route = useRoute();
        const settingsStore = useSettingsStore();
        /**
         * Watch for changes in feature flags.
         * When INTERCOM_WIDGET is enabled, initialize Intercom through the settings store.
         */
        watch(() => (settingsStore.featureFlags[INTERCOM_WIDGET_FLAG] ?? false), isIntercomEnabled => {
            if (isIntercomEnabled && !settingsStore.isIntercomInitialized) {
                settingsStore.initializeIntercom();
            }
        }, { immediate: true });
        /**
         * Apply Intercom visibility rules based on current route.
         * Extracted to avoid duplication between watchers.
         */
        function applyIntercomVisibility() {
            if (!settingsStore.isIntercomInitialized ||
                !window.Intercom ||
                typeof route.name !== 'string') {
                return;
            }
            const routeName = route.name;
            if (ROUTE_WHITELIST.includes(routeName)) {
                window.Intercom('update', { hide_default_launcher: false });
            }
            else {
                window.Intercom('hide'); // Close the messenger if open
                window.Intercom('update', { hide_default_launcher: true }); // Hide the launcher
            }
        }
        /**
         * Watch for route changes to control Intercom visibility.
         * Show Intercom only on whitelisted routes.
         */
        watch(() => route.name, applyIntercomVisibility, { immediate: true });
        /**
         * Watch for Intercom initialization to apply visibility rules immediately.
         * This ensures correct visibility on page refresh when Intercom initializes after route is set.
         */
        watch(() => settingsStore.isIntercomInitialized, (isInitialized) => {
            if (isInitialized) {
                applyIntercomVisibility();
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1));
        };
    }
});
